// 纸感花艺装饰:全部为内联矢量(stroke/fill 随香研社纸金配色定稿),不引入外部图片资源
type OrnamentProps = {
  className?: string
  flip?: boolean
}

const STEM = '#c9a87c'
const LEAF_A = '#a3b18a'
const LEAF_B = '#94a37e'
const PETAL = '#d8a79e'
const PETAL_DEEP = '#c98a7a'
const BERRY = '#b98d6f'

// 单片叶:从原点向右伸展的杏仁形
const LEAF_D = 'M0 0 Q6 -6 14 -4 Q7 2 0 0 Z'

function Leaf({ x, y, r = 0, s = 1, color = LEAF_A, flip = false }: { x: number; y: number; r?: number; s?: number; color?: string; flip?: boolean }) {
  return (
    <path
      d={LEAF_D}
      transform={`translate(${x} ${y}) rotate(${r}) scale(${flip ? -s : s} ${s})`}
      fill={color}
      opacity={0.9}
    />
  )
}

function Flower({ x, y, s = 1, petal = PETAL, center = STEM }: { x: number; y: number; s?: number; petal?: string; center?: string }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {[0, 72, 144, 216, 288].map((angle) => (
        <ellipse key={angle} cx={0} cy={-4.6} rx={2.3} ry={3.9} fill={petal} transform={`rotate(${angle})`} opacity={0.92} />
      ))}
      <circle r={2} fill={center} />
    </g>
  )
}

// 页宽藤蔓花带:置于页首/页脚边缘,全局所有页面可见
export function GarlandBand({ className, flip = false }: OrnamentProps) {
  const clusters = Array.from({ length: 20 }, (_, i) => i)
  const flowers = [3, 10, 17]

  return (
    <svg
      viewBox="0 0 1440 32"
      className={className}
      style={flip ? { transform: 'scaleY(-1)' } : undefined}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      focusable="false"
    >
      <path
        d={clusters
          .map((i) => {
            const x0 = i * 72
            return i === 0 ? `M0 16 Q 36 6 72 16` : `Q ${x0 + 36} ${i % 2 === 0 ? 6 : 26} ${x0 + 72} 16`
          })
          .join(' ')}
        fill="none"
        stroke={STEM}
        strokeWidth={1.1}
        opacity={0.75}
      />
      {clusters.map((i) => {
        const x = 36 + i * 72
        const up = i % 2 === 0
        const baseR = up ? -64 : 64
        const color = i % 4 < 2 ? LEAF_A : LEAF_B
        return (
          <g key={i}>
            <Leaf x={x} y={16} r={baseR - 24} s={0.72} color={color} flip={up} />
            <Leaf x={x} y={16} r={baseR} s={0.95} color={color} flip={up} />
            <Leaf x={x} y={16} r={baseR + 24} s={0.72} color={color} flip={up} />
          </g>
        )
      })}
      {flowers.map((i) => (
        <Flower key={i} x={36 + i * 72} y={16} s={1.05} petal={i === 10 ? PETAL_DEEP : PETAL} />
      ))}
    </svg>
  )
}

// 完整花环:环形叶圈 + 底部花簇,可旋转作强调(如专辑封面外圈)
export function WreathRing({ className, spin = false }: { className?: string; spin?: boolean }) {
  const leaves = Array.from({ length: 18 }, (_, i) => i * 20)
  return (
    <svg
      viewBox="0 0 160 160"
      className={className}
      style={spin ? { animation: 'orn-spin 46s linear infinite' } : undefined}
      aria-hidden
      focusable="false"
    >
      {leaves.map((angle) => (
        <g key={angle} transform={`rotate(${angle} 80 80)`}>
          <path
            d={LEAF_D}
            transform="translate(80 11) rotate(62)"
            fill={angle % 40 === 0 ? LEAF_B : LEAF_A}
            opacity={0.88}
          />
        </g>
      ))}
      <g transform="rotate(156 80 80)">
        <Flower x={80} y={12} s={1.15} petal={PETAL} />
      </g>
      <g transform="rotate(204 80 80)">
        <Flower x={80} y={12} s={1.15} petal={PETAL_DEEP} />
      </g>
      {[174, 180, 186].map((angle) => (
        <g key={angle} transform={`rotate(${angle} 80 80)`}>
          <circle cx={80} cy={13} r={1.8} fill={BERRY} opacity={0.9} />
        </g>
      ))}
    </svg>
  )
}

// 角落藤蔓(四分之一花环):用于卡片/框架四角,默认画的是左上角朝向
export function CornerSprig({ className, flip = false }: OrnamentProps) {
  const stops: Array<[number, number, number, boolean]> = [
    [13, 92, -52, false],
    [21, 77, -46, true],
    [31, 62, -40, false],
    [42, 49, -34, true],
    [55, 38, -26, false],
    [70, 26, -18, true],
    [85, 16, -10, false],
  ]
  return (
    <svg
      viewBox="0 0 110 110"
      className={className}
      style={flip ? { transform: 'scaleX(-1)' } : undefined}
      aria-hidden
      focusable="false"
    >
      <path d="M6 104 C 18 72, 40 38, 90 9" fill="none" stroke={STEM} strokeWidth={1.2} strokeLinecap="round" opacity={0.8} />
      {stops.map(([x, y, r, side]) => (
        <Leaf key={`${x}-${y}`} x={x} y={y} r={r} s={0.92} color={side ? LEAF_B : LEAF_A} flip={side} />
      ))}
      <circle cx={9} cy={99} r={2} fill={BERRY} opacity={0.85} />
      <circle cx={16} cy={103} r={1.6} fill={PETAL_DEEP} opacity={0.8} />
      <Flower x={93} y={8} s={1} petal={PETAL_DEEP} />
    </svg>
  )
}

// 花艺分隔线:中央小花 + 两侧对称渐隐藤叶
export function BotanicalDivider({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 320 26" className={className} aria-hidden focusable="false">
      <path d="M148 13 C 122 5, 96 21, 68 13 S 24 5, 6 13" fill="none" stroke={STEM} strokeWidth={1.1} opacity={0.7} />
      <path d="M172 13 C 198 5, 224 21, 252 13 S 296 5, 314 13" fill="none" stroke={STEM} strokeWidth={1.1} opacity={0.7} />
      {[
        [126, 10, -18, LEAF_A, false],
        [104, 16, 12, LEAF_B, true],
        [78, 11, -12, LEAF_A, false],
        [46, 13, 6, LEAF_B, true],
        [194, 10, 18, LEAF_A, true],
        [216, 16, -12, LEAF_B, false],
        [242, 11, 12, LEAF_A, true],
        [274, 13, -6, LEAF_B, false],
      ].map(([x, y, r, color, side], i) => (
        <Leaf key={i} x={x as number} y={y as number} r={r as number} s={0.85 - i * 0.04} color={color as string} flip={side as boolean} />
      ))}
      <Flower x={160} y={13} s={1.5} petal={PETAL_DEEP} />
    </svg>
  )
}
